import * as React from "react";
import { GetFaQsByEventQuery } from "@src/@types/api.d";
import { useFaqList } from "@src/hooks/useFaqList";
import { motion, AnimatePresence } from "framer-motion";

export type IFAQ = {
  eventId: number;
  faqData: GetFaQsByEventQuery;
  loading?: boolean;
};

const FAQ: React.FC<IFAQ> = ({ eventId, faqData }) => {
  const {
    faqItems,
    paginatedFaqItems,
    currentPage,
    totalPages,
    openIndex,
    toggleFAQ,
    handlePageChange,
  } = useFaqList({
    faqData,
    itemsPerPage: 3,
    resetOpenOnPageChange: true,
  });

  // Return null if no FAQs exist
  if (!faqItems.length) return null;

  return (
    <div
      className="w-full min-h-[40vh] md:min-h-[70vh] py-16 lg:py-20 overflow-hidden relative border-b"
      style={{
        background:
          "linear-gradient(135deg, rgba(255, 182, 193, 0.1) 0%, rgba(255, 240, 245, 0.1) 50%, rgba(230, 230, 250, 0.1) 100%), #f4fdfd",
        boxShadow: "inset 0px 24px 84px rgba(255, 246, 224, 0.25)",
      }}
    >
      {/* Header */}
      <div className="text-center mb-12 relative z-10">
        <h2 className="text-black text-3xl md:text-4xl lg:text-5xl font-normal leading-tight tracking-wide">
          Q & A
        </h2>
      </div>

      {/* FAQ Container */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-col gap-4 md:gap-5">
          {paginatedFaqItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full text-left p-5 md:p-6 lg:p-7 flex items-center justify-between gap-4 cursor-pointer transition-colors duration-300 hover:bg-gray-50"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h3 className="text-base md:text-lg lg:text-xl font-semibold text-black leading-tight tracking-wide flex-1">
                  {item.question}
                </h3>

                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6 text-[#EEAD06]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </motion.div>
              </button>

              {/* Answer */}
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    id={`faq-answer-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 md:px-6 lg:px-7 pb-5 md:pb-6 lg:pb-7 pt-0">
                      <div className="border-t border-gray-200 pt-4 md:pt-5">
                        <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed whitespace-pre-line">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 md:gap-3 mt-8 md:mt-12 relative z-15">
          {/* Previous Button */}
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 md:px-5 md:py-2.5 rounded-lg bg-white text-[#EEAD06] border-2 border-[#EEAD06] font-semibold cursor-pointer transition-all duration-300 hover:enabled:bg-[#EEAD06] hover:enabled:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Page indicator */}
          <span className="text-black font-semibold px-2">
            {currentPage} / {totalPages}
          </span>

          {/* Next Button */}
          <button
            onClick={() =>
              handlePageChange(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
            className="px-4 py-2 md:px-5 md:py-2.5 rounded-lg bg-white text-[#EEAD06] border-2 border-[#EEAD06] font-semibold cursor-pointer transition-all duration-300 hover:enabled:bg-[#EEAD06] hover:enabled:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default FAQ;
